import {useContext, useEffect, useReducer, useState} from "react"
import isEqual from "lodash/isEqual"
import omit from "lodash/omit"

import NotificationsContext from "../contexts/notifications.js"
import FetcherContext from "../contexts/fetcher.js"

import useInterval from "./interval.js"

const UNNECESSARY_OPTIONS_FOR_FETCH = [
  "nocache",
  "restartWhenAuthChanges",
  "polling",
  "pollingIteration",
  "pollingParameter",
]

const reducer = (state, action) => {
  const {type, payload} = action
  if (process.env.__DEBUG_FETCHER__ === "true") {
    if (state?.options?.debug || payload?.options?.debug) {
      // eslint-disable-next-line no-console
      console.log({url: state.url, type, payload, state})
    }
  }
  switch (type) {
    case "FETCH_INIT":
      // if (
      //   payload.url &&
      //   payload.options &&
      //   state.url === payload.url &&
      //   state.options === payload.options
      // ) {
      //   return state
      // }
      return {
        ...state,
        ...("url" in payload && {url: payload.url}),
        ...("options" in payload && {options: payload.options}),
        loading: true,
        error: null,
        fetched: false,
      }
    case "FETCH_SUCCESS": {
      if (state?.options?.polling && state.options.pollingIteration > 0) {
        if (isEqual(state.data, payload.data)) {
          return state
        }
        if (Object.values(payload.data).every(v => v === true)) {
          return state
        }
      }
      return {
        ...state,
        data: payload.data,
        loading: false,
        error: null,
        fetched: true,
      }
    }
    case "FETCH_FAILURE":
      return {
        ...state,
        data: null,
        loading: false,
        error: payload.error,
        fetched: true,
      }
    case "SET_URL":
      if (payload.url && state.url === payload.url) return state
      return {
        ...state,
        ...("url" in payload && {url: payload.url}),
        ...("options" in payload && {options: payload.options}),
        error: null,
        fetched: false,
      }
  }
}

const cache = new Map()
const getResource = async (url, options) => {
  const key = JSON.stringify({url, options})
  if (!cache.has(key) || (options && options.nocache)) {
    const res = await fetch(url, omit(options, UNNECESSARY_OPTIONS_FOR_FETCH))
    cache.set(key, res)
  } else {
    if (process.env.__DEBUG_FETCHER__ === "true") {
      // eslint-disable-next-line no-console
      console.log(`GET RESSOURCE FROM CACHE -- ${url}`)
    }
  }
  return new Promise(resolve => {
    resolve(cache.get(key))
  })
}

const prependURL = (url, api) => {
  if (url.startsWith("http")) return url
  if (url.startsWith("/api"))
    return `${api.slice(0, api.indexOf("/api/v1.1"))}${url}`
  return `${api}${url}`
}

// TODO(nyalab): add timeout, loader, store?, ok
const useFetcher = (url, options) => {
  let initialState = {
    url: url || null,
    options: options || null,
    loading: false,
    data: null,
    error: null,
    fetched: false,
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const {api, options: fetchOptions} = useContext(FetcherContext)
  const {addError} = useContext(NotificationsContext)

  const [pollingIteration, setPollingIteration] = useState()

  useInterval(
    () => {
      let canceled = false
      let pollingURL = prependURL(state.url, api)
      if (state?.options?.pollingParameter) {
        pollingURL += `&${state.options.pollingParameter}=${Date.now()}`
      }
      getResource(pollingURL, {
        ...fetchOptions,
        ...state.options,
        nocache: true,
      })
        .then(async res => {
          const data = await res.clone().json()
          if (!isEqual(state.data, data)) {
            if (data === false) {
              dispatch({type: "FETCH_FAILURE", payload: {error: true}})
              return
            }
            dispatch({type: "FETCH_SUCCESS", payload: {data}})
          }
        })
        .catch(error => {
          if (!canceled) {
            dispatch({type: "FETCH_FAILURE", payload: {error}})
            addError("FETCH_FAILURE")
          }
        })
        .finally(() => {
          setPollingIteration(pollingIteration + 1)
        })

      return () => {
        canceled = true
      }
    },
    pollingIteration ? state.options.polling : null
  )

  useEffect(() => {
    if (!state.url) return
    let canceled = false

    dispatch({
      type: "FETCH_INIT",
      payload: {url: state.url, options: state.options},
    })

    getResource(prependURL(state.url, api), {
      ...fetchOptions,
      ...state.options,
    })
      .then(async res => {
        const data = await res.clone().json()
        if (data === false) {
          dispatch({type: "FETCH_FAILURE", payload: {error: true}})
          return
        }
        dispatch({type: "FETCH_SUCCESS", payload: {data}})
      })
      .catch(error => {
        if (!canceled) {
          dispatch({type: "FETCH_FAILURE", payload: {error}})
          addError("FETCH_FAILURE")
        }
      })
      .finally(() => {
        if (state?.options?.polling) setPollingIteration(1)
      })

    return () => {
      canceled = true
    }
  }, [state.url, state.options])

  useEffect(() => {
    if (state.url === url && isEqual(state.options, options)) return
    if (url && url.indexOf("token") > -1 && !options) {
      options = {
        headers: {
          "Content-Type": "text/plain;charset=UTF-8",
        },
      }
    }
    dispatch({type: "SET_URL", payload: {url, options}})
  }, [url, JSON.stringify(options)])

  return state
}

export default useFetcher
